import {fitWithin} from './fit';

// A share card: the photo with the words written on it, because phone share sheets drop the
// text when a file is attached (WhatsApp keeps only the image). Oat band at the foot in the
// app's own type: the title, one meta line, and a small "Sow and Tell" mark. Built on a canvas
// from the already-resized photo, so it is quick and never bigger than 1600 px.

export interface CardWords {
    title: string;
    meta: string;
}

const INK = '#22251E';
const INK_2 = '#5B6054';
const OAT = '#F3F1EA';
const SHOOT = '#2E7A38';
const DISPLAY = "'Bricolage Grotesque', system-ui, sans-serif";
const BODY = "'Figtree', system-ui, sans-serif";

async function decode(file: Blob): Promise<{source: CanvasImageSource; width: number; height: number; close(): void}> {
    if ('createImageBitmap' in window) {
        const bitmap = await createImageBitmap(file, {imageOrientation: 'from-image'});
        return {source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close()};
    }
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decode'));
        el.src = url;
    });
    return {source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url)};
}

/** Breaks text into lines that fit, keeping at most `maxLines` (the last one gets an ellipsis). */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
        const trial = line ? `${line} ${word}` : word;
        if (ctx.measureText(trial).width <= maxWidth || !line) {
            line = trial;
        } else {
            lines.push(line);
            line = word;
        }
        if (lines.length === maxLines) break;
    }
    if (lines.length < maxLines && line) lines.push(line);
    if (lines.length === maxLines && (words.join(' ') !== lines.join(' '))) {
        let last = lines[maxLines - 1];
        while (last && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1).trimEnd();
        lines[maxLines - 1] = `${last}…`;
    }
    return lines;
}

export async function composeShareCard(photo: Blob, words: CardWords, fileName = 'sow-and-tell.jpg'): Promise<File> {
    await Promise.all([document.fonts.load(`750 40px ${DISPLAY}`), document.fonts.load(`500 24px ${BODY}`)]).catch(() => undefined);
    const decoded = await decode(photo);
    try {
        const {width, height} = fitWithin(decoded.width, decoded.height, 1600);
        const pad = Math.round(width * 0.045);
        const titleSize = Math.round(width * 0.052);
        const metaSize = Math.round(width * 0.03);
        const markSize = Math.round(width * 0.026);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no canvas');

        // Measure the band first so the canvas is exactly photo + band.
        ctx.font = `750 ${titleSize}px ${DISPLAY}`;
        const titleLines = wrap(ctx, words.title, width - pad * 2, 2);
        const bandHeight = pad + titleLines.length * Math.round(titleSize * 1.15) + (words.meta ? Math.round(metaSize * 1.9) : Math.round(metaSize * 0.6)) + Math.round(markSize * 2.2) + pad * 0.6;

        canvas.width = width;
        canvas.height = height + Math.round(bandHeight);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(decoded.source, 0, 0, width, height);

        ctx.fillStyle = OAT;
        ctx.fillRect(0, height, width, Math.round(bandHeight));

        let y = height + pad + titleSize;
        ctx.fillStyle = INK;
        ctx.font = `750 ${titleSize}px ${DISPLAY}`;
        ctx.textBaseline = 'alphabetic';
        for (const line of titleLines) {
            ctx.fillText(line, pad, y);
            y += Math.round(titleSize * 1.15);
        }
        if (words.meta) {
            y += Math.round(metaSize * 0.7);
            ctx.fillStyle = INK_2;
            ctx.font = `500 ${metaSize}px ${BODY}`;
            ctx.fillText(wrap(ctx, words.meta, width - pad * 2, 1)[0] ?? '', pad, y);
        }
        const markY = height + Math.round(bandHeight) - pad * 0.6 - Math.round(markSize * 0.4);
        ctx.fillStyle = SHOOT;
        ctx.font = `700 ${markSize}px ${DISPLAY}`;
        ctx.fillText('Sow and Tell', pad, markY);

        const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob'))), 'image/jpeg', 0.86));
        return new File([blob], fileName, {type: 'image/jpeg'});
    } finally {
        decoded.close();
    }
}
